import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Leaf } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { getCondition, CONDITIONS } from "@/data/conditions";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { SEO } from "@/components/Seo";

export default function ConditionPage() {
  const { slug } = useParams();
  const condition = getCondition(slug);
  if (!condition) return <Navigate to="/science" replace />;

  const others = CONDITIONS.filter((c) => c.slug !== slug).slice(0, 3);

  return (
    <div data-testid="science-detail-page">
      <SEO
        title={`${condition.title} — Plain-Language Guide | Pranivaa Kitchens`}
        description={`${condition.tagline} An Indian-kitchen-first guide with pantry allies and FAQs.`}
        path={`/science/${condition.slug}`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: condition.title,
          description: condition.tagline,
          image: condition.image,
          author: { "@type": "Organization", name: "Pranivaa Kitchens", url: "https://pranivaakitchens.com" },
        }}
      />
      <section className="pt-28 md:pt-36 pb-10 bg-paper-muted/50 border-b border-[#E7E2D7]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Link
              to="/science"
              data-testid="condition-back-link"
              className="inline-flex items-center gap-2 text-xs font-semibold tracking-wide uppercase text-ink-muted hover:text-beet transition-colors mb-8"
            >
              <ArrowLeft size={14} /> All guides
            </Link>
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-clay font-semibold mb-4">
              The Science Desk
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl tracking-tight leading-[1.08] text-ink font-semibold mb-5">
              {condition.title}
            </h1>
            <p className="text-lg leading-relaxed text-ink-soft">{condition.intro}</p>
          </Reveal>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <img loading="lazy" decoding="async"
              src={condition.image}
              alt={condition.imageAlt}
              className="w-full h-64 md:h-96 object-cover rounded-3xl shadow-lg mb-14"
            />
          </Reveal>
          {condition.sections.map((s, i) => (
            <Reveal key={i} className="mb-12">
              <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-ink tracking-tight mb-4 flex items-baseline gap-3">
                <span className="font-mono text-sm text-clay font-semibold">{String(i + 1).padStart(2, "0")}</span>
                {s.h}
              </h2>
              {s.p.map((para, j) => (
                <p key={j} className="text-base leading-relaxed text-ink-soft mb-4 last:mb-0">{para}</p>
              ))}
            </Reveal>
          ))}

          <Reveal className="my-14 bg-leaf-light/60 border border-leaf/20 rounded-3xl p-8 md:p-10">
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-leaf font-semibold mb-6 flex items-center gap-2">
              <Leaf size={14} /> Pantry Allies
            </p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {condition.foods.map((f) => (
                <li key={f.name} className="bg-paper-card rounded-xl border border-[#E7E2D7] p-4">
                  <p className="font-semibold text-sm text-ink">{f.name}</p>
                  <p className="text-xs text-ink-muted mt-1">{f.why}</p>
                </li>
              ))}
            </ul>
          </Reveal>

          <Reveal className="my-14">
            <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-ink tracking-tight mb-6">
              Asked often, answered honestly.
            </h2>
            <Accordion type="single" collapsible className="w-full" data-testid="condition-faq-accordion">
              {condition.faqs.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-[#E7E2D7]">
                  <AccordionTrigger
                    data-testid={`condition-faq-question-${i}`}
                    className="font-serif text-lg font-semibold text-ink text-left hover:text-beet"
                  >
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm leading-relaxed text-ink-soft">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{
                __html: JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "FAQPage",
                  mainEntity: condition.faqs.map((f) => ({
                    "@type": "Question",
                    name: f.q,
                    acceptedAnswer: { "@type": "Answer", text: f.a },
                  })),
                }),
              }}
            />
          </Reveal>

          <Reveal className="my-12">
            <p className="text-xs font-mono text-ink-muted leading-relaxed border-l-2 border-turmeric pl-4">
              This guide is educational and follows FSSAI advertising norms — it does not diagnose,
              treat, cure or prevent any disease. Please work with your physician on any condition.
            </p>
          </Reveal>

          <Reveal className="my-14 bg-beet rounded-3xl p-8 md:p-10 text-center">
            <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-white mb-3">
              Want this science on your plate?
            </h2>
            <p className="text-sm text-white/70 mb-7 max-w-md mx-auto">
              Fresh, clean veg & vegan meals are coming to Delhi NCR and Bengaluru. The waitlist gets
              first access.
            </p>
            <Link
              to="/coming-soon"
              data-testid="condition-cta-waitlist"
              className="inline-flex items-center gap-2 rounded-full bg-turmeric text-ink font-bold text-sm px-7 py-4 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl"
            >
              Join the Waitlist <ArrowRight size={15} />
            </Link>
          </Reveal>

          <div>
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-clay font-semibold mb-6">
              Keep Reading
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {others.map((o) => (
                <Link
                  key={o.slug}
                  to={`/science/${o.slug}`}
                  className="group bg-paper-card border border-[#E7E2D7] rounded-xl p-5 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
                >
                  <p className="font-serif text-lg font-semibold text-ink leading-snug mb-1">{o.navName}</p>
                  <p className="text-xs text-ink-muted leading-relaxed">{o.tagline}</p>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
